import {
  markBookingSyncDisabled,
  markBookingSyncError,
  markBookingSyncPending,
  markBookingSyncSuccess,
  normalizeBooking,
} from './booking';
import {
  getDefaultCalendarConnectionForOwner,
  normalizeCalendarConnection,
} from './calendarConnections';
import {
  createGoogleCalendarEvent,
  updateGoogleCalendarEvent,
  cancelGoogleCalendarEvent,
  getGoogleCalendarConflicts,
} from './googleCalendarProvider';
import {
  createOutlookCalendarEvent,
  updateOutlookCalendarEvent,
  cancelOutlookCalendarEvent,
  getOutlookCalendarConflicts,
} from './outlookCalendarProvider';

function normalizeString(value = '') {
  return String(value || '').trim();
}

function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function rangesOverlap(startA, endA, startB, endB) {
  const aStart = toDateValue(startA)?.getTime?.();
  const aEnd = toDateValue(endA)?.getTime?.();
  const bStart = toDateValue(startB)?.getTime?.();
  const bEnd = toDateValue(endB)?.getTime?.();

  if ([aStart, aEnd, bStart, bEnd].some((value) => typeof value !== 'number' || Number.isNaN(value))) {
    return false;
  }

  return aStart < bEnd && bStart < aEnd;
}

function buildOpaqueRef(prefix = 'calendar') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeProvider(value = '') {
  const provider = normalizeString(value).toLowerCase();
  if (['google', 'outlook', 'apple', 'ics'].includes(provider)) return provider;
  return provider ? 'generic' : '';
}

function getProviderAdapter(provider = '') {
  if (provider === 'google') {
    return {
      create: createGoogleCalendarEvent,
      update: updateGoogleCalendarEvent,
      cancel: cancelGoogleCalendarEvent,
      getConflicts: getGoogleCalendarConflicts,
    };
  }

  if (provider === 'outlook') {
    return {
      create: createOutlookCalendarEvent,
      update: updateOutlookCalendarEvent,
      cancel: cancelOutlookCalendarEvent,
      getConflicts: getOutlookCalendarConflicts,
    };
  }

  return null;
}

export function resolveBookingCalendarConnection(
  booking = {},
  {
    provider = '',
    externalCalendarRef = '',
    calendarConnection = null,
    calendarConnections = [],
  } = {}
) {
  if (calendarConnection) {
    return normalizeCalendarConnection(calendarConnection);
  }

  const normalizedBooking = normalizeBookingSafely(booking);
  const normalizedProvider = normalizeProvider(provider || normalizedBooking.calendarProvider);
  const normalizedExternalCalendarRef = normalizeString(externalCalendarRef || normalizedBooking.externalCalendarRef);
  const normalizedConnections = (calendarConnections || []).map((connection) => normalizeCalendarConnection(connection));

  if (normalizedExternalCalendarRef) {
    const matchingByRef = normalizedConnections.find(
      (connection) => connection.externalCalendarRef === normalizedExternalCalendarRef
    );
    if (matchingByRef) return matchingByRef;
  }

  const defaultConnection = getDefaultCalendarConnectionForOwner(normalizedConnections, {
    provider: normalizedProvider,
  });
  if (defaultConnection) return defaultConnection;

  if (normalizedProvider && normalizedProvider !== 'ics' && normalizedProvider !== 'apple') {
    return normalizeCalendarConnection({
      ownerType: 'user',
      ownerId: normalizedBooking.instructorId || normalizedBooking.requesterUserId || '',
      provider: normalizedProvider,
      externalCalendarRef: normalizedExternalCalendarRef,
      status: 'connected',
      syncEnabled: true,
      metadata: {
        provisionalConnection: true,
      },
    });
  }

  return null;
}

function normalizeFallbackBooking(booking = {}) {
  return {
    bookingId: booking.bookingId || booking.id || '',
    organizationId: booking.organizationId || '',
    instructorId: booking.instructorId || '',
    requesterUserId: booking.requesterUserId || '',
    requesterName: booking.requesterName || '',
    requesterEmail: booking.requesterEmail || '',
    serviceType: booking.serviceType || 'Session',
    notes: booking.notes || '',
    startAt: booking.startAt || null,
    endAt: booking.endAt || null,
    timezone: booking.timezone || 'UTC',
    locationLabel: booking.locationLabel || '',
    status: booking.status || 'confirmed',
    calendarProvider: booking.calendarProvider || '',
    externalCalendarRef: booking.externalCalendarRef || '',
    externalEventId: booking.externalEventId || '',
    syncStatus: booking.syncStatus || 'not_connected',
    syncAttemptCount: booking.syncAttemptCount || 0,
    syncErrorMessage: booking.syncErrorMessage || '',
  };
}

function normalizeBookingSafely(booking = {}) {
  return normalizeBooking(booking) || normalizeFallbackBooking(booking);
}

function escapeIcsText(value = '') {
  return String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

function formatIcsDate(value) {
  const date = toDateValue(value);
  if (!date) return '';
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
}

export function normalizeBookingCalendarState(record = {}) {
  const provider = normalizeProvider(record.calendarProvider || '');
  const rawStatus = normalizeString(record.syncStatus || '');
  const normalizedStatus = rawStatus === 'not_synced'
    ? 'not_connected'
    : (rawStatus || (provider ? 'pending' : 'not_connected'));

  return {
    externalCalendarRef: normalizeString(record.externalCalendarRef || ''),
    externalEventId: normalizeString(record.externalEventId || ''),
    calendarProvider: provider,
    syncStatus: normalizedStatus,
    syncAttemptCount: Number(record.syncAttemptCount || 0) || 0,
    lastSyncedAt: record.lastSyncedAt || null,
    syncErrorMessage: normalizeString(record.syncErrorMessage || ''),
  };
}

export function buildBookingCalendarPayload(booking = {}) {
  const normalizedBooking = normalizeBookingSafely(booking);
  return {
    bookingId: normalizedBooking.bookingId,
    organizationId: normalizedBooking.organizationId,
    instructorId: normalizedBooking.instructorId,
    requesterUserId: normalizedBooking.requesterUserId,
    title: normalizedBooking.serviceType || 'Coaching Session',
    description: [
      normalizedBooking.requesterName ? `Requester: ${normalizedBooking.requesterName}` : '',
      normalizedBooking.notes ? `Notes: ${normalizedBooking.notes}` : '',
      normalizedBooking.meetingType ? `Meeting type: ${normalizedBooking.meetingType}` : '',
    ].filter(Boolean).join('\n'),
    startAt: normalizedBooking.startAt,
    endAt: normalizedBooking.endAt,
    timezone: normalizedBooking.timezone || 'UTC',
    locationLabel: normalizedBooking.locationLabel || '',
    status: normalizedBooking.status || 'confirmed',
  };
}

export async function createExternalCalendarEvent(
  booking = {},
  {
    provider = '',
    externalCalendarRef = '',
    calendarConnection = null,
    calendarConnections = [],
  } = {}
) {
  const normalizedBooking = normalizeBookingSafely(booking);
  const connection = resolveBookingCalendarConnection(normalizedBooking, {
    provider,
    externalCalendarRef,
    calendarConnection,
    calendarConnections,
  });
  const normalizedProvider = normalizeProvider(provider || connection?.provider || normalizedBooking.calendarProvider);
  if (!normalizedProvider || normalizedProvider === 'ics' || normalizedProvider === 'apple') {
    return {
      ok: false,
      skipped: true,
      provider: normalizedProvider || '',
      reason: 'provider_connection_not_available',
    };
  }
  const providerAdapter = getProviderAdapter(normalizedProvider);
  if (!providerAdapter) {
    return {
      ok: false,
      skipped: true,
      provider: normalizedProvider,
      reason: 'provider_not_supported',
    };
  }

  const result = await providerAdapter.create(normalizedBooking, connection || {});
  return {
    ...result,
    payload: buildBookingCalendarPayload(normalizedBooking),
  };
}

export async function updateExternalCalendarEvent(
  booking = {},
  {
    provider = '',
    externalCalendarRef = '',
    externalEventId = '',
    calendarConnection = null,
    calendarConnections = [],
  } = {}
) {
  const normalizedBooking = normalizeBookingSafely(booking);
  const connection = resolveBookingCalendarConnection(normalizedBooking, {
    provider,
    externalCalendarRef,
    calendarConnection,
    calendarConnections,
  });
  const normalizedProvider = normalizeProvider(provider || connection?.provider || normalizedBooking.calendarProvider);
  if (!normalizedProvider || normalizedProvider === 'ics' || normalizedProvider === 'apple') {
    return {
      ok: false,
      skipped: true,
      provider: normalizedProvider || '',
      reason: 'provider_connection_not_available',
    };
  }
  const providerAdapter = getProviderAdapter(normalizedProvider);
  if (!providerAdapter) {
    return {
      ok: false,
      skipped: true,
      provider: normalizedProvider,
      reason: 'provider_not_supported',
    };
  }

  const result = await providerAdapter.update(
    {
      ...normalizedBooking,
      externalEventId: externalEventId || normalizedBooking.externalEventId || buildOpaqueRef(`evt_${normalizedProvider}`),
    },
    connection || {}
  );

  return {
    ...result,
    payload: buildBookingCalendarPayload(normalizedBooking),
  };
}

export async function cancelExternalCalendarEvent(
  booking = {},
  {
    provider = '',
    externalCalendarRef = '',
    externalEventId = '',
    calendarConnection = null,
    calendarConnections = [],
  } = {}
) {
  const normalizedBooking = normalizeBookingSafely(booking);
  const connection = resolveBookingCalendarConnection(normalizedBooking, {
    provider,
    externalCalendarRef,
    calendarConnection,
    calendarConnections,
  });
  const normalizedProvider = normalizeProvider(provider || connection?.provider || normalizedBooking.calendarProvider);
  if (!normalizedProvider || normalizedProvider === 'ics' || normalizedProvider === 'apple') {
    return {
      ok: false,
      skipped: true,
      provider: normalizedProvider || '',
      reason: 'provider_connection_not_available',
    };
  }
  const providerAdapter = getProviderAdapter(normalizedProvider);
  if (!providerAdapter) {
    return {
      ok: false,
      skipped: true,
      provider: normalizedProvider,
      reason: 'provider_not_supported',
    };
  }

  return providerAdapter.cancel(
    {
      ...normalizedBooking,
      externalEventId: externalEventId || normalizedBooking.externalEventId || buildOpaqueRef(`evt_${normalizedProvider}`),
    },
    connection || {}
  );
}

export function getExternalCalendarConflicts(
  booking = {},
  {
    externalEvents = [],
    excludeEventId = '',
    provider = '',
    externalCalendarRef = '',
    calendarConnection = null,
    calendarConnections = [],
  } = {}
) {
  const normalizedBooking = normalizeBookingSafely(booking);
  const connection = resolveBookingCalendarConnection(normalizedBooking, {
    provider,
    externalCalendarRef,
    calendarConnection,
    calendarConnections,
  });
  const providerAdapter = getProviderAdapter(normalizeProvider(provider || connection?.provider || normalizedBooking.calendarProvider));
  const providerBackedEvents = providerAdapter?.getConflicts
    ? providerAdapter.getConflicts(connection || {}, normalizedBooking, { excludeEventId })
    : [];

  return [...(externalEvents || []), ...(providerBackedEvents || [])]
    .filter(Boolean)
    .filter((event) => normalizeString(event.status || 'confirmed') !== 'canceled')
    .filter((event) => normalizeString(event.externalEventId || event.id) !== excludeEventId)
    .filter((event) => rangesOverlap(
      event.startAt,
      event.endAt,
      normalizedBooking.startAt,
      normalizedBooking.endAt
    ));
}

export function doesExternalCalendarConflict(booking = {}, options = {}) {
  return getExternalCalendarConflicts(booking, options).length > 0;
}

export function blockBookingIfExternalConflict(booking = {}, options = {}) {
  const conflicts = getExternalCalendarConflicts(booking, options);
  if (conflicts.length > 0) {
    throw new Error('The selected booking time conflicts with an external calendar event.');
  }
  return { ok: true, conflicts: [] };
}

export async function handleBookingCalendarCreate(
  db,
  {
    booking = {},
    provider = '',
    externalCalendarRef = '',
    externalEvents = [],
    calendarConnection = null,
    calendarConnections = [],
  } = {}
) {
  const normalizedBooking = normalizeBookingSafely(booking);
  const connection = resolveBookingCalendarConnection(normalizedBooking, {
    provider,
    externalCalendarRef,
    calendarConnection,
    calendarConnections,
  });
  const normalizedProvider = normalizeProvider(provider || connection?.provider || normalizedBooking.calendarProvider);

  if (!normalizedProvider) {
    return markBookingSyncDisabled(db, {
      booking: normalizedBooking,
      syncStatus: 'not_connected',
      syncErrorMessage: '',
    });
  }

  try {
    blockBookingIfExternalConflict(normalizedBooking, {
      externalEvents,
      provider: normalizedProvider,
      externalCalendarRef,
      calendarConnection: connection,
      calendarConnections,
    });
    await markBookingSyncPending(db, {
      booking: normalizedBooking,
      calendarProvider: normalizedProvider,
      externalCalendarRef: connection?.externalCalendarRef || externalCalendarRef,
    });

    const result = await createExternalCalendarEvent(normalizedBooking, {
      provider: normalizedProvider,
      externalCalendarRef: connection?.externalCalendarRef || externalCalendarRef,
      calendarConnection: connection,
      calendarConnections,
    });

    if (!result.ok && result.skipped) {
      return markBookingSyncDisabled(db, {
        booking: normalizedBooking,
        syncStatus: 'disabled',
        syncErrorMessage: result.reason || '',
      });
    }

    return markBookingSyncSuccess(db, {
      booking: normalizedBooking,
      calendarProvider: result.provider,
      externalCalendarRef: result.externalCalendarRef,
      externalEventId: result.externalEventId,
    });
  } catch (error) {
    return markBookingSyncError(db, {
      booking: normalizedBooking,
      syncErrorMessage: error?.message || 'Calendar sync failed.',
    });
  }
}

export async function handleBookingCalendarUpdate(
  db,
  {
    booking = {},
    provider = '',
    externalCalendarRef = '',
    externalEventId = '',
    externalEvents = [],
    calendarConnection = null,
    calendarConnections = [],
  } = {}
) {
  const normalizedBooking = normalizeBookingSafely(booking);
  const connection = resolveBookingCalendarConnection(normalizedBooking, {
    provider,
    externalCalendarRef,
    calendarConnection,
    calendarConnections,
  });
  const normalizedProvider = normalizeProvider(provider || connection?.provider || normalizedBooking.calendarProvider);

  if (!normalizedProvider) {
    return markBookingSyncDisabled(db, {
      booking: normalizedBooking,
      syncStatus: 'not_connected',
      syncErrorMessage: '',
    });
  }

  try {
    blockBookingIfExternalConflict(normalizedBooking, {
      externalEvents,
      excludeEventId: externalEventId || normalizedBooking.externalEventId,
      provider: normalizedProvider,
      externalCalendarRef,
      calendarConnection: connection,
      calendarConnections,
    });
    await markBookingSyncPending(db, {
      booking: normalizedBooking,
      calendarProvider: normalizedProvider,
      externalCalendarRef: connection?.externalCalendarRef || externalCalendarRef,
      externalEventId,
    });

    const result = await updateExternalCalendarEvent(normalizedBooking, {
      provider: normalizedProvider,
      externalCalendarRef: connection?.externalCalendarRef || externalCalendarRef,
      externalEventId,
      calendarConnection: connection,
      calendarConnections,
    });

    if (!result.ok && result.skipped) {
      return markBookingSyncDisabled(db, {
        booking: normalizedBooking,
        syncStatus: 'disabled',
        syncErrorMessage: result.reason || '',
      });
    }

    return markBookingSyncSuccess(db, {
      booking: normalizedBooking,
      calendarProvider: result.provider,
      externalCalendarRef: result.externalCalendarRef,
      externalEventId: result.externalEventId,
    });
  } catch (error) {
    return markBookingSyncError(db, {
      booking: normalizedBooking,
      syncErrorMessage: error?.message || 'Calendar update failed.',
    });
  }
}

export async function handleBookingCalendarCancel(
  db,
  {
    booking = {},
    provider = '',
    externalCalendarRef = '',
    externalEventId = '',
    calendarConnection = null,
    calendarConnections = [],
  } = {}
) {
  const normalizedBooking = normalizeBookingSafely(booking);
  const connection = resolveBookingCalendarConnection(normalizedBooking, {
    provider,
    externalCalendarRef,
    calendarConnection,
    calendarConnections,
  });
  const normalizedProvider = normalizeProvider(provider || connection?.provider || normalizedBooking.calendarProvider);

  if (!normalizedProvider) {
    return markBookingSyncDisabled(db, {
      booking: normalizedBooking,
      syncStatus: 'not_connected',
      syncErrorMessage: '',
    });
  }

  try {
    await markBookingSyncPending(db, {
      booking: normalizedBooking,
      calendarProvider: normalizedProvider,
      externalCalendarRef: connection?.externalCalendarRef || externalCalendarRef,
      externalEventId,
    });

    const result = await cancelExternalCalendarEvent(normalizedBooking, {
      provider: normalizedProvider,
      externalCalendarRef: connection?.externalCalendarRef || externalCalendarRef,
      externalEventId,
      calendarConnection: connection,
      calendarConnections,
    });

    if (!result.ok && result.skipped) {
      return markBookingSyncDisabled(db, {
        booking: normalizedBooking,
        syncStatus: 'disabled',
        syncErrorMessage: result.reason || '',
      });
    }

    return markBookingSyncSuccess(db, {
      booking: {
        ...normalizedBooking,
        status: 'canceled',
      },
      calendarProvider: result.provider,
      externalCalendarRef: result.externalCalendarRef,
      externalEventId: result.externalEventId,
    });
  } catch (error) {
    return markBookingSyncError(db, {
      booking: normalizedBooking,
      syncErrorMessage: error?.message || 'Calendar cancellation failed.',
    });
  }
}

export async function syncBookingToCalendar(
  db,
  {
    booking = {},
    action = 'create',
    provider = '',
    externalCalendarRef = '',
    externalEventId = '',
    externalEvents = [],
    calendarConnection = null,
    calendarConnections = [],
  } = {}
) {
  if (action === 'cancel') {
    return handleBookingCalendarCancel(db, {
      booking,
      provider,
      externalCalendarRef,
      externalEventId,
      calendarConnection,
      calendarConnections,
    });
  }

  if (action === 'update') {
    return handleBookingCalendarUpdate(db, {
      booking,
      provider,
      externalCalendarRef,
      externalEventId,
      externalEvents,
      calendarConnection,
      calendarConnections,
    });
  }

  return handleBookingCalendarCreate(db, {
    booking,
    provider,
    externalCalendarRef,
    externalEvents,
    calendarConnection,
    calendarConnections,
  });
}

export function generateBookingICS(booking = {}) {
  const normalizedBooking = normalizeBookingSafely(booking);
  const nowStamp = formatIcsDate(new Date());
  const startStamp = formatIcsDate(normalizedBooking.startAt);
  const endStamp = formatIcsDate(normalizedBooking.endAt);
  const filename = `${normalizedBooking.bookingId || 'booking'}.ics`;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Multi Tenant Coaching Platform//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${escapeIcsText(normalizedBooking.bookingId || buildOpaqueRef('booking'))}@coaching-platform`,
    `DTSTAMP:${nowStamp}`,
    startStamp ? `DTSTART:${startStamp}` : '',
    endStamp ? `DTEND:${endStamp}` : '',
    `SUMMARY:${escapeIcsText(normalizedBooking.serviceType || 'Coaching Session')}`,
    `DESCRIPTION:${escapeIcsText([
      normalizedBooking.requesterName ? `Requester: ${normalizedBooking.requesterName}` : '',
      normalizedBooking.notes || '',
    ].filter(Boolean).join('\n'))}`,
    normalizedBooking.locationLabel ? `LOCATION:${escapeIcsText(normalizedBooking.locationLabel)}` : '',
    `STATUS:${normalizedBooking.status === 'canceled' ? 'CANCELLED' : 'CONFIRMED'}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  const content = `${lines.join('\r\n')}\r\n`;
  return {
    filename,
    content,
    mimeType: 'text/calendar;charset=utf-8',
  };
}

export function downloadBookingICS(
  booking = {},
  {
    documentRef = typeof document !== 'undefined' ? document : null,
    windowRef = typeof window !== 'undefined' ? window : null,
  } = {}
) {
  const ics = generateBookingICS(booking);
  if (!documentRef || !windowRef || typeof Blob === 'undefined' || typeof windowRef.URL?.createObjectURL !== 'function') {
    return ics;
  }

  const blob = new Blob([ics.content], { type: ics.mimeType });
  const url = windowRef.URL.createObjectURL(blob);
  const anchor = documentRef.createElement('a');
  anchor.href = url;
  anchor.download = ics.filename;
  if (typeof anchor.click === 'function') {
    anchor.click();
  }
  if (typeof windowRef.URL.revokeObjectURL === 'function') {
    windowRef.URL.revokeObjectURL(url);
  }
  return {
    ...ics,
    url,
  };
}
